import { useEffect, useState } from 'react';
import { api, ApiFailure, type Dataset } from '../api';
import { Notice } from './Panels';

interface TemplateSummary {
  id: string;
  vertical: string;
  name: string;
  description: string;
  icon: string;
  priceCents: number;
  planRequired: string;
  fieldCount: number;
  chartCount: number;
}

interface TemplateMatch {
  templateId: string;
  name: string;
  icon: string;
  score: number;
  matchedFields: string[];
  missingRequired: string[];
}

/**
 * Template marketplace — browse and apply pre-built Looker Studio dashboards
 * for specific verticals. Users can match templates against their dataset's
 * columns to find the best fit.
 */
interface TemplateDetail {
  id: string;
  name: string;
  description: string;
  icon: string;
  expectedFields: { name: string; aliases: string[]; type: string; role: string; required: boolean }[];
  charts: { id: string; type: string; title: string; dimension?: string; metric?: string }[];
  calculatedFields: { name: string; formula: string; why: string }[];
}

export function TemplateMarketplace({ dataset }: { dataset: Dataset | null; plan?: string }) {
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [matches, setMatches] = useState<TemplateMatch[]>([]);
  const [selected, setSelected] = useState<TemplateSummary | null>(null);
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [applied, setApplied] = useState<{ template: unknown; fieldMapping: Record<string, string>; warnings: string[] } | null>(null);

  useEffect(() => {
    api.listTemplates()
      .then((r) => setTemplates(r.templates))
      .catch((e) => setFailure(asFailure(e)));
  }, []);

  useEffect(() => {
    if (dataset === null) { setMatches([]); return; }
    const columns = dataset.columns.map((c) => c.name);
    api.matchTemplates(columns).then((r) => setMatches(r.matches)).catch(() => {});
  }, [dataset]);

  async function viewDetail(id: string): Promise<void> {
    setFailure(null);
    try {
      const r = await api.getTemplateDetail(id);
      setDetail(r.template as TemplateDetail);
    } catch (e) { setFailure(asFailure(e)); }
  }

  async function applyTemplate(templateId: string): Promise<void> {
    if (dataset === null) return;
    setBusy(true);
    setFailure(null);
    setApplied(null);
    try {
      const r = await api.applyTemplate(templateId, dataset.id);
      setApplied({ template: r.template, fieldMapping: r.fieldMapping, warnings: r.warnings });
    } catch (e) { setFailure(asFailure(e)); }
    finally { setBusy(false); }
  }

  function downloadTemplate(): void {
    if (applied === null) return;
    const blob = new Blob([JSON.stringify(applied.template, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${(selected?.name ?? 'template').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.looker-template.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="page">
      <header className="intro">
        <h2>Template Marketplace</h2>
        <p>
          {templates.length === 0
            ? 'Loading the template catalogue…'
            : `${templates.length} pre-built dashboards for specific industries. Match against your data, apply a template, and export a finished Looker Studio dashboard.`}
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {matches.length > 0 && dataset !== null && (
        <section className="card">
          <header>
            <h2>Recommended for your data</h2>
            <span className="sub">Based on your column names</span>
          </header>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginTop: 12 }}>
            {matches.slice(0, 3).map((m) => {
              const tpl = templates.find((t) => t.id === m.templateId);
              return (
                <div key={m.templateId} className="card" style={{ padding: 16, cursor: 'pointer', borderColor: 'var(--accent, #2563eb)' }}
                  onClick={() => { if (tpl) setSelected(tpl); void viewDetail(m.templateId); }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 24 }}>{m.icon}</span>
                    <div>
                      <strong>{m.name}</strong>
                      <div className="muted" style={{ fontSize: 12 }}>{(m.score * 100).toFixed(0)}% match</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted, #6b7280)' }}>
                    Matched: {m.matchedFields.join(', ')}
                    {m.missingRequired.length > 0 && (
                      <div style={{ color: '#b45309', marginTop: 4 }}>Missing: {m.missingRequired.join(', ')}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="card">
        <header>
          <h2>All templates</h2>
          <span className="sub">{templates.length} available</span>
        </header>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginTop: 12 }}>
          {templates.map((t) => (
            <div
              key={t.id}
              className="card"
              style={{ padding: 16, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
              onClick={() => { setSelected(t); void viewDetail(t.id); }}
              onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 28 }}>{t.icon}</span>
                <div>
                  <strong>{t.name}</strong>
                  <div className="muted" style={{ fontSize: 12 }}>{t.vertical.replace('-', ' ')}</div>
                </div>
              </div>
              <p style={{ fontSize: 13, margin: '0 0 8px', color: 'var(--muted, #6b7280)' }}>{t.description}</p>
              <div style={{ fontSize: 12, color: 'var(--muted, #6b7280)' }}>
                {t.chartCount} charts · {t.fieldCount} fields
              </div>
              <div style={{ marginTop: 8 }}>
                {t.priceCents === 0 ? (
                  <span className="badge accent">Included</span>
                ) : (
                  <span className="badge">${(t.priceCents / 100).toFixed(0)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {selected !== null && (
        <section className="card" style={{ marginTop: 16 }}>
          <header>
            <h2>{selected.icon} {selected.name}</h2>
            <button className="ghost tiny" onClick={() => { setSelected(null); setDetail(null); setApplied(null); }}>Close</button>
          </header>

          <p>{selected.description}</p>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {selected.priceCents === 0 ? (
              <span className="badge accent">Included</span>
            ) : (
              <span className="badge">${(selected.priceCents / 100).toFixed(0)}</span>
            )}
            <span className="badge">{selected.chartCount} charts</span>
            <span className="badge">{selected.fieldCount} fields</span>
          </div>

          {detail !== null && (
            <>
              {detail.charts.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 8 }}>Charts included</h3>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>
                    {detail.charts.map((chart) => (
                      <li key={chart.id}>
                        <strong>{chart.title}</strong>
                        <span className="muted"> — {chart.type}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {detail.expectedFields.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <h3 style={{ fontSize: 14, marginBottom: 8 }}>Expected fields</h3>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {detail.expectedFields.map((field) => (
                      <span key={field.name} className="badge">
                        {field.name}
                        {field.required ? '' : ' (optional)'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {applied !== null && (
            <div style={{ marginTop: 12 }}>
              <Notice tone="good" message="Template applied successfully." />
              {applied.warnings.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {applied.warnings.map((w, i) => <Notice key={i} message={w} />)}
                </div>
              )}

              <div style={{ marginTop: 12 }}>
                <h3 style={{ fontSize: 14, marginBottom: 8 }}>Field mapping</h3>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Template field</th><th>Your column</th></tr></thead>
                    <tbody>
                      {Object.entries(applied.fieldMapping).map(([field, column]) => (
                        <tr key={field}><td>{field}</td><td><code>{column}</code></td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="primary" onClick={downloadTemplate}>Download template (.json)</button>
                <span className="muted" style={{ fontSize: 12.5, maxWidth: 480 }}>
                  Import it in Looker Studio via <em>File → Import report configuration</em> after connecting
                  your data source.
                </span>
              </div>
            </div>
          )}

          {applied === null && (
            <div style={{ marginTop: 12 }}>
              {dataset === null ? (
                <p className="muted">Upload a dataset first to apply this template.</p>
              ) : (
                <button className="primary" onClick={() => applyTemplate(selected.id)} disabled={busy}>
                  {busy ? <span className="spinner" /> : null}
                  Apply to "{dataset.name}"
                </button>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function asFailure(error: unknown): ApiFailure {
  return error instanceof ApiFailure ? error : new ApiFailure('The request failed.', 'Try again.', 0);
}
